import styled from 'styled-components'
import { useEffect } from 'react'
import useTranslation from '../../hooks/useTranslation'
import Loader from '../generic/Loader'
import DetailViewLines from './DetailViewLines'
import { renderCardGroups } from './common'
import ActionLines from './ActionLines'

const CardList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
`

const DetailCardWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`

const DetailCard = styled.div`
    display: flex;
    flex-direction: column;
    background: ${({ theme }) => theme?.card?.background || '#ffffff'};
    border-radius: 12px;
    box-shadow: ${({ theme }) => theme?.card?.boxShadow || '0 2px 8px rgba(0, 0, 0, 0.1)'};
    padding: 1rem;
    gap: 0.5rem;
`

const DetailCardHeader = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
`

const DetailTitle = styled.h3`
    font-size: var(--font-M);
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: ${({ theme }) => theme?.font?.color || '#333'};
`

const EmptyMessage = styled.div`
    text-align: center;
    padding: 2rem;
    color: ${({ theme }) => theme?.font?.label || '#515559'};
    font-style: italic;
    background: ${({ theme }) => theme?.card?.background || '#ffffff'};
    border-radius: 12px;
    box-shadow: ${({ theme }) => theme?.card?.boxShadow || '0 2px 8px rgba(0, 0, 0, 0.1)'};
`

/**
 * DetailView Component
 * 
 * Displays detail entities as individual cards with optional lines/sub-cards.
 * 
 * Props:
 * - config: Configuration object with:
 *   - title: Optional title for the detail section
 *   - card: Object with group array for card content
 *   - lines: Configuration for nested line items
 *   - actions: Array of conditional action lines
 * - data: Array of detail items to display
 * - isLoading: Loading state
 * - onAction: Handler for line item actions
 * - pageState: Current page state (contains master data)
 * - useStore: Store hook for modal state management
 * - masterData: The master entity data (e.g., invoice) for condition checking
 */
const DetailView = ({ config, data, isLoading, onAction, pageState, useStore, masterData }) => {
    const { t } = useTranslation()
    const [state, setState] = useStore((s) => s)
    
    // Update the store with master data so StateForm can access the invoice id
    // This ensures the id is available when the modal opens
    useEffect(() => {
        if (masterData && masterData.id && masterData.id !== state?.id) {
            setState(prev => ({
                ...prev,
                ...masterData,
                entity: prev?.entity || pageState?.entity
            }))
        }
    }, [masterData?.id])
    
    if (isLoading) {
        return <Loader />
    }
    
    if (!config) {
        return null
    }
    
    const cardGroups = config.card?.group || []
    const linesConfig = config.lines
    const actionsConfig = config.actions || []
    const items = Array.isArray(data) ? data : []
    
    return (
        <>
            {config.title && <DetailTitle>{t(config.title)}</DetailTitle>}
            {items.length === 0 ? (
                <EmptyMessage>{t('common.noData')}</EmptyMessage>
            ) : (
                <CardList>
                    {items.map((item, index) => (
                        <DetailCardWrapper key={item.id || index}>
                            <DetailCard>
                                <DetailCardHeader>
                                    {renderCardGroups(cardGroups, item, t)}
                                </DetailCardHeader>
                            </DetailCard>
                            <DetailViewLines 
                                config={linesConfig} 
                                item={item} 
                                onAction={onAction} 
                            />
                        </DetailCardWrapper>
                    ))}
                    
                    {/* Render action lines using the new component */}
                    <ActionLines 
                        actions={actionsConfig}
                        masterData={masterData}
                        detailData={items[0]}
                        useStore={useStore}
                        onUpdate={onAction}
                    />
                </CardList>
            )}
        </>
    )
}

export default DetailView
